import {checkExists} from 'external/dev_april_corgi+/js/common/asserts';

import {LoadPlateAction} from '../types/actions';
import {PlateId} from '../types/data';
import {PlateState} from '../types/lab';

import {ActionsBuffer, ActionsDispatcher} from './actions_dispatcher';
import {NodeId} from './graph';
import {Source} from './pipes';

export const PLATE_IDS = {
  eppendorfs: 'epdfs' as PlateId,
  fourCelsius: 'fourc' as PlateId,
} as const;

export class LabState {
  private readonly plates: Map<PlateId, PlateState>;
  private lastId: number;

  readonly presetPlates: {
    eppendorfs: PlateId;
    fourCelsius: PlateId;
    magnetic: PlateId;
    thermocycling: PlateId;
  };

  constructor(private readonly sandbox: boolean) {
    this.plates = new Map();
    this.lastId = -1;

    this.presetPlates = {
      eppendorfs: 'epdfs' as PlateId,
      fourCelsius: 'fourc' as PlateId,
      magnetic: 'magnt' as PlateId,
      thermocycling: 'therm' as PlateId,
    };
  }

  async initialize(dispatcher: ActionsDispatcher): Promise<void> {
    const pipe: Source<unknown> = {
      id: {id: '0', node: 'deck-setup' as NodeId},
      act: () => Promise.resolve(),
      lastValue: () => undefined,
      link: () => {},
      links: () => [],
    };
    dispatcher.pipeBlockedOn(pipe, []);
    const requests: Array<{
      id: PlateId;
      capabilities: LoadPlateAction['capabilities'];
    }> = [
      {
        id: this.presetPlates.eppendorfs,
        capabilities: {wellVolumeNl: 1_500_000},
      },
      {
        id: this.presetPlates.fourCelsius,
        capabilities: {temperature: true},
      },
      {id: this.presetPlates.magnetic, capabilities: {magnetic: true}},
      {
        id: this.presetPlates.thermocycling,
        capabilities: {thermocycling: true},
      },
    ];
    const promises = requests.map(({id, capabilities}) =>
      dispatcher
        .actor(pipe)
        .loadPlate(id, capabilities)
        .then(r => [id, r] as const),
    );
    const results = await Promise.all(promises);
    for (const [id, result] of results) {
      this.plates.set(
        id,
        new PlateState(id, this.sandbox, result.maxWellVolumeNl),
      );
    }
    dispatcher.pipeFinished(pipe);
  }

  plate(id: PlateId): PlateState {
    return checkExists(this.plates.get(id));
  }

  async reservePlate(buffer: ActionsBuffer): Promise<PlateState> {
    const id = String(++this.lastId) as PlateId;
    const result = await buffer.loadPlate(id, {});
    const plate = new PlateState(
      String(++this.lastId) as PlateId,
      this.sandbox,
      result.maxWellVolumeNl,
    );
    this.plates.set(plate.id, plate);
    return plate;
  }
}

export function plateIdToLabel(id: PlateId): string {
  if (id === PLATE_IDS.eppendorfs) {
    return 'Eppendorfs';
  } else if (id === PLATE_IDS.fourCelsius) {
    return '4° C';
  } else {
    throw new Error(`Unknown label for plate ${id}`);
  }
}
